import { apiClient, getActivePvzId, isSupabaseConfigured } from './_shared.js'

export async function getAiSettings() {
  const pvzId = getActivePvzId()
  if (!pvzId) return null
  try {
    const pvz = await apiClient(`/pvz/${pvzId}`)
    if (!pvz || !pvz.org_id) return null
    return await apiClient(`/organizations/${pvz.org_id}/ai_settings`)
  } catch {
    return null
  }
}

export async function saveAiSettings(settings) {
  const pvzId = getActivePvzId()
  if (!pvzId) return null
  try {
    const pvz = await apiClient(`/pvz/${pvzId}`)
    if (!pvz || !pvz.org_id) return null
    return await apiClient(`/organizations/${pvz.org_id}/ai_settings`, {
      method: 'PUT',
      body: JSON.stringify(settings)
    })
  } catch (err) {
    throw err
  }
}

export async function getAISettings(orgId) { return getAiSettings(); }
export async function updateAISettings(orgId, settings) { return saveAiSettings(settings); }

// ============================================
// AI PERMISSIONS (Phase 2)
// ============================================

const DEFAULT_ADMIN_PERMISSIONS = {
  // Зарплаты и финансы
  salaries: true,          // Зарплаты всех сотрудников
  expenses: true,          // Расходы ПВЗ
  incomes: false,          // Доходы ПВЗ (по умолчанию ВЫКЛ для админа)
  financial_summary: false, // P&L / Чистая прибыль (по умолчанию ВЫКЛ для админа)
  // Команда и график
  employees: true,         // Данные сотрудников
  schedule: true,          // Полный график
  employee_management: true, // Управление командой
  // Операции
  operations: true,        // Приёмка / выдача / возвраты
  tasks: true,             // Задачи
  supplies: true,          // Заявки на хознужды
  disputes: true,          // Оспаривания штрафов
  // ИИ
  ai_advice: true,         // Советы и рекомендации ИИ
  settings: false          // Настройки ПВЗ
}

const DEFAULT_EMPLOYEE_PERMISSIONS = {
  // Личные данные
  own_salary: true,        // Своя зарплата
  own_shifts: true,        // Свои смены (логи часов)
  own_tasks: true,         // Свои задачи
  own_operations: true,    // Свои операции (приёмка/выдача)
  // Общие данные
  general_schedule: true,  // Общий график (кто когда работает)
  work_rules: true,        // Правила WB и работы на ПВЗ
  // Закрытые разделы
  salaries: false,         // Зарплаты всех
  employees: false,        // Данные других сотрудников
  expenses: false,         // Расходы
  incomes: false,          // Доходы
  financial_summary: false, // P&L
  operations: false,       // Все операции
  tasks: false,            // Все задачи
  supplies: false,         // Заявки на хознужды
  disputes: false,         // Оспаривания
  ai_advice: false,        // Советы
  employee_management: false,
  settings: false
}

export async function getAiPermissions() {
  if (!isSupabaseConfigured) return { admin_permissions: DEFAULT_ADMIN_PERMISSIONS, employee_permissions: DEFAULT_EMPLOYEE_PERMISSIONS }
  const pvzId = getActivePvzId()
  if (!pvzId) return { admin_permissions: DEFAULT_ADMIN_PERMISSIONS, employee_permissions: DEFAULT_EMPLOYEE_PERMISSIONS }
  
  try {
    const me = await apiClient('/auth/me')
    if (!me || !me.org_id) return { admin_permissions: DEFAULT_ADMIN_PERMISSIONS, employee_permissions: DEFAULT_EMPLOYEE_PERMISSIONS }
    
    const data = await apiClient(`/organizations/${me.org_id}/ai_permissions`)
    if (!data) return { admin_permissions: DEFAULT_ADMIN_PERMISSIONS, employee_permissions: DEFAULT_EMPLOYEE_PERMISSIONS }
    
    return {
      admin_permissions: { ...DEFAULT_ADMIN_PERMISSIONS, ...(data.admin_permissions || {}) },
      employee_permissions: { ...DEFAULT_EMPLOYEE_PERMISSIONS, ...(data.employee_permissions || {}) },
    }
  } catch {
    return { admin_permissions: DEFAULT_ADMIN_PERMISSIONS, employee_permissions: DEFAULT_EMPLOYEE_PERMISSIONS }
  }
}

export async function saveAiPermissions(adminPerms, employeePerms) {
  try {
    const me = await apiClient('/auth/me')
    if (!me || !me.org_id) return
    
    await apiClient(`/organizations/${me.org_id}/ai_permissions`, {
      method: 'PUT',
      body: JSON.stringify({
        admin_permissions: adminPerms,
        employee_permissions: employeePerms
      })
    })
  } catch(e) {
    console.error(e)
  }
}

// ============================================
// AI CONVERSATIONS (Phase 3)
// ============================================

export async function getAiConversations() {
  const pvzId = getActivePvzId()
  return await apiClient(`/ai/conversations?pvzId=${pvzId}`) || []
}

export async function getAiConversation(id) {
  return await apiClient(`/ai/conversations/${id}`)
}

export async function createAiConversation(title, messages, model) {
  const pvzId = getActivePvzId()
  return await apiClient('/ai/conversations', {
    method: 'POST',
    body: JSON.stringify({ pvz_id: pvzId, title, messages, model })
  })
}

export async function updateAiConversation(id, updates) {
  return await apiClient(`/ai/conversations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  })
}

export async function deleteAiConversation(id) {
  await apiClient(`/ai/conversations/${id}`, { method: 'DELETE' })
}
